
import React from "react";
import Header from "@/components/Header";
import { ScrollToTopButton } from "@/components/ui/scroll-to-top";
import { motion } from "framer-motion";
import { PageSEO } from "@/components/seo/PageSEO";

const Testimonials = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <PageSEO 
        title="Customer Testimonials | Retail Ranker"
        description="Read what our customers are saying about Retail Ranker - the software that helps brick & mortar shops improve their Google rankings."
        keywords="retail store testimonials, shop reviews, customer success stories, Google ranking help"
      />
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-20 pb-12 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 z-0"></div>
          <div className="container px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">Our Customer Success Stories</h1>
              <p className="text-lg text-muted-foreground">
                See how real retailers are growing their businesses with Retail Ranker
              </p>
            </motion.div>
          </div>
        </section>
        
        {/* Testimonials */}
        <section className="py-12 md:py-16">
          <div className="container px-4 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Testimonial 1 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl shadow-sm p-6 md:p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">JD</div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Jane Doe</h3>
                    <p className="text-sm text-muted-foreground">Boutique Owner in Portland</p>
                  </div>
                </div>
                <blockquote className="mt-4 text-muted-foreground">
                  "Since using Retail Ranker, my shop appears in the top 3 results for local searches. My foot traffic has increased by 40% in just three months!"
                </blockquote>
              </motion.div>
              
              {/* Testimonial 2 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl shadow-sm p-6 md:p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">MS</div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Mike Smith</h3>
                    <p className="text-sm text-muted-foreground">Hardware Store Manager in Chicago</p>
                  </div>
                </div>
                <blockquote className="mt-4 text-muted-foreground">
                  "The competitor analysis feature is gold! I discovered exactly what was holding us back and fixed it. Now we outrank the big box stores for our main keywords."
                </blockquote>
              </motion.div>
              
              {/* Testimonial 3 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl shadow-sm p-6 md:p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">AJ</div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Alicia Johnson</h3>
                    <p className="text-sm text-muted-foreground">Bookstore Owner in Austin</p>
                  </div>
                </div>
                <blockquote className="mt-4 text-muted-foreground">
                  "I was skeptical at first, but the results speak for themselves. Our review response time improved, our rankings climbed, and our store is thriving again!"
                </blockquote>
              </motion.div>
              
              {/* Testimonial 4 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl shadow-sm p-6 md:p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">RP</div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Robert Peterson</h3>
                    <p className="text-sm text-muted-foreground">Gift Shop Owner in Denver</p>
                  </div>
                </div>
                <blockquote className="mt-4 text-muted-foreground">
                  "The ROI on Retail Ranker is incredible. For every $1 I spend, I'm seeing about $12 back in new business. It's the best investment I've made in my shop."
                </blockquote>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-lg mb-4">Ready to join these success stories?</p>
              <a 
                href="/signup" 
                className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 py-2 text-sm font-medium transition-colors"
              >
                Start Free Trial
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      
      
      <ScrollToTopButton />
    </div>
  );
};

export default Testimonials;
